import { createAsyncActions, runAsyncWorkflow } from '../lib/reduxHelpers'
import { Offline, ReadingProgram } from '../api'
import { createAction } from "redux-actions";
import { MAX_OFFLINE_BOOKS_COUNT } from '../lib/constants';

export const [
  unsaveOffline,
  receiveUnsaveOffline,
  unsaveOfflineError
] = createAsyncActions(
  'UNSAVE_OFFLINE',
  ({ book, userId }) => ({ book, userId }),
  ({ book, userId }) => ({ book, userId }),
);

export const unsaveOfflineWorkflow = (book, imageSize = 6, userId, themeNumber = null) => {
  let type = book.isFromReadingProgram ? 'rp' : book.isFromFlpHome ? 'flpHome' : book.isFromStemHome ? 'stemHome' : null
  const fetchPromise = Offline.unsave(book.slug, imageSize, userId, type, themeNumber);

  return runAsyncWorkflow(
    {
      fetch: unsaveOffline,
      receive: receiveUnsaveOffline,
      error: unsaveOfflineError
    },
    fetchPromise,
    { book, userId }
  );
};

export const [
  saveOffline,
  receiveSaveOffline,
  saveOfflineError
] = createAsyncActions(
  'SAVE_OFFLINE',
  ({ book, userId }) => ({ book, userId }),
  ({ book, userId }) => ({ book, userId }),
);

export const saveOfflineWorkflow = (book, imageSize = 6, userId, enrollmentId, themeNumber) => {
  let type = book.isFromReadingProgram ? 'rp' : book.isFromFlpHome ? 'flpHome' : book.isFromStemHome ? 'stemHome': null
  const fetchPromise = Offline.save(book.slug, imageSize, userId, type, enrollmentId, themeNumber, book.locale);

  return runAsyncWorkflow(
    {
      fetch: saveOffline,
      receive: receiveSaveOffline,
      error: saveOfflineError
    },
    fetchPromise,
    { book, userId }
  );
};

export const [
  saveOfflineTranslations,
  receiveSaveOfflineTranslations,
  saveOfflineTranslationsError
] = createAsyncActions(
  'SAVE_OFFLINE_TRANSLATIONS',
  ({ book, userId, translationContent }) => ({ book, userId, translationContent }),
  ({ book, userId, translationContent }) => ({ book, userId, translationContent }),
);

export const saveOfflineTranslationsWorkflow = (book, imageSize = 6, userId, translationContent) => {
  const fetchPromise = Offline.saveTranslation(book.slug, imageSize, userId, translationContent);

  return runAsyncWorkflow(
    {
      fetch: saveOfflineTranslations,
      receive: receiveSaveOfflineTranslations,
      error: saveOfflineTranslationsError
    },
    fetchPromise,
    { book, userId, translationContent }
  );
};

export const [
  updateOfflineTranslations,
  receiveUpdateOfflineTranslations,
  updateOfflineTranslationsError
] = createAsyncActions(
  'UPDATE_OFFLINE_TRANSLATIONS',
  ({ book, userId, translationContent }) => ({ book, userId, translationContent }),
  ({ book, userId, translationContent }) => ({ book, userId, translationContent }),
);

export const updateOfflineTranslationsWorkflow = (book, userId, translationContent) => {
  const fetchPromise = Offline.updateTranslation(book.id, userId, translationContent);

  return runAsyncWorkflow(
    {
      fetch: updateOfflineTranslations,
      receive: receiveUpdateOfflineTranslations,
      error: updateOfflineTranslationsError
    },
    fetchPromise,
    { book, userId, translationContent }
  );
};

export const [
  unsaveOfflineTranslations,
  receiveUnsaveOfflineTranslations,
  unsaveOfflineTranslationsError
] = createAsyncActions(
  'UNSAVE_OFFLINE_TRANSLATIONS',
  ({ book, userId }) => ({ book, userId }),
  ({ book, userId }) => ({ book, userId }),
);

export const unsaveOfflineTranslationsWorkflow = (book, imageSize = 6, userId, language) => {
  const fetchPromise = Offline.unsaveTranslation(book.slug, imageSize, userId, language);

  return runAsyncWorkflow(
    {
      fetch: unsaveOfflineTranslations,
      receive: receiveUnsaveOfflineTranslations,
      error: unsaveOfflineTranslationsError
    },
    fetchPromise,
    { book, userId }
  );
};

export const [
  fetchOfflineTranslations,
  receiveOfflineTranslations,
  receiveOfflineTranslationsError
] = createAsyncActions(
  'OFFLINE_TRANSLATIONS',
  ({ book, userId }) => ({ book, userId }),
  ({ book, userId }) => ({ book, userId }),
);

export const fetchOfflineTranslationsWorkflow = (book, userId, language) => {
  const fetchPromise = Offline.fetchTranslation(book.id, userId, language);

  return runAsyncWorkflow(
    {
      fetch: fetchOfflineTranslations,
      receive: receiveOfflineTranslations,
      error: receiveOfflineTranslationsError
    },
    fetchPromise,
    { book, userId }
  );
};

export const isAvailableOffline = (bookId, offlineBooks, tab = 'read', userId, lpId) => {
  let books = [];

  switch (tab) {
    // 'Reading programme' tab
    case 'rp':
      books = offlineBooks.books.filter(offlineBook => offlineBook.isFromReadingProgram && offlineBook.id === bookId);
      break;

    // 'Flp' tab
    case 'flpHome':
      books = offlineBooks.books.filter(offlineBook => offlineBook.isFromFlpHome && offlineBook.id === bookId);
      break;
  
    // 'stem' tab
    case 'stemHome':
      books = offlineBooks.books.filter(offlineBook => offlineBook.isFromStemHome && offlineBook.id === bookId && offlineBook.lpId === lpId);
      break;
  
    // 'Translate' tab
    case 'translate':
      books = offlineBooks.books.filter(offlineBook => offlineBook.isFromOfflineTranslate && offlineBook.id === bookId);
      break;

    // 'Read' tab
    case 'read':
    default:
      books = offlineBooks.books.filter(offlineBook => !offlineBook.isFromOfflineTranslate && !offlineBook.isFromReadingProgram && !offlineBook.isFromFlpHome && !offlineBook.isFromStemHome && offlineBook.id === bookId);
  }

  if(userId) {
    return books.filter(book => book.savedUsers && book.savedUsers.includes(userId)).length;
  }
  return books.length;
}

export const isTranslationAvailable = (bookId, translations, userId, targetLanguage) => {
  return translations.filter(translation => (translation.id === bookId) && (translation.userId === userId) && (translation.targetLanguage === targetLanguage)).length
}

export const onTabChange = createAction('ON_TAB_CHANGE');

export const getOfflineBooksCount = (offlineBooksCountForUser, offlineBooksCount, isAvailableOffline) => {
  let booksCount = offlineBooksCountForUser;
  if(offlineBooksCountForUser < MAX_OFFLINE_BOOKS_COUNT) {
    if(!isAvailableOffline) booksCount = offlineBooksCount;
  }
  return booksCount;
}

export const getOfflineBooksCountForList = (offlineBooksCountForUser, booksToSaveByUser, offlineBooksCount, booksToSaveOffline) => {
  const booksCountAfterSaveForOffline = offlineBooksCount + booksToSaveOffline;
  const booksCountAfterSaveForUser = offlineBooksCountForUser + booksToSaveByUser;

  let booksCount = booksCountAfterSaveForOffline;
  if (!booksToSaveOffline) {
    if (booksCountAfterSaveForUser < MAX_OFFLINE_BOOKS_COUNT) booksCount = booksCountAfterSaveForUser;
  }

  return booksCount;
}

export const [
  updateTheme,
  receiveTheme,
  updateThemeError
] = createAsyncActions(
  'UPDATE_THEME_DETAILS',
  () => ({}),
  ({ book, data }) => ({ book, data })
);

export const updateThemeDetails = (book) => {
  const user = book.enrolledUsers[0];
  const fetchPromise = ReadingProgram.getThemeDetails(user.enrollmentId, parseInt(user.themeNumber, 10));

  return runAsyncWorkflow(
    {
      fetch: updateTheme,
      receive: receiveTheme,
      error: updateThemeError
    }, 
    fetchPromise,
    { book }
  );
};



// WEBPACK FOOTER //
// ./src/redux/offlineBooksActions.js